import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, ArrowUpRight, X, CheckCheck, Clock, Radio, Receipt, MapPin } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { ehComercialInterno } from '../lib/departamentos';
import { useModalDetalheAberto } from '../lib/negotiationModalSignal';
import { ehNotificacaoDeLeadAtribuido, ordenarNotificacoesLead, LeadAssignedMetadata } from '../lib/leadAssignedNotifications';
import { Notification } from '../../types';
import { propsFundoModal } from '../lib/backdropModal';

const FAIXA_CONTA_LABEL: Record<string, string> = {
    'acima_de_r$700': 'Acima de R$700',
    'de_r$300_a_r$700': 'R$300 a R$700',
    'menos_de_r$300': 'Menos de R$300',
};

const fmtHorario = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const fmtOrigem = (m: LeadAssignedMetadata): string | null => {
    const partes = [m.plataforma, m.campanha, m.anuncio, m.formulario].filter(Boolean);
    if (partes.length === 0) return m.origem || null;
    return partes.join(' · ');
};

/**
 * Aviso de "novo lead atribuído" para Closers e BDRs — em tempo real, sem F5
 * (Realtime sobre `notifications`, mesmo padrão do NotificationDropdown.tsx).
 *
 * Regras (spec do dono do produto, ago/2026):
 *  - Não pode aparecer por cima do NegotiationDetailModal aberto — represa
 *    (continua acumulando via realtime) e só mostra quando ele fechar
 *    (useModalDetalheAberto).
 *  - Vários leads quase juntos viram uma lista dentro do MESMO popup, nunca
 *    um popup sobrepondo o outro. Itens novos entram no FIM da lista — nunca
 *    reordena o que já está renderizado, pra não fazer quem está lendo perder
 *    a posição.
 *  - Fechar o popup (X, clique fora, Esc) só minimiza — não grava `read=true`.
 *    Isso é de propósito: gravar read aqui desligaria o pulso do card no
 *    Kanban (views/CRM/Kanban/KanbanCard.tsx), o reforço que existe
 *    justamente pra não perder um lead que foi dispensado sem ser aberto.
 *    Só duas ações gravam read=true: abrir o lead (NegotiationDetailModal.tsx)
 *    e "Marcar tudo como visto" abaixo, os dois conscientes.
 */
const LeadAssignedPopup: React.FC = () => {
    const { user, profile } = useAuth();
    const navigate = useNavigate();
    const modalDetalheAberto = useModalDetalheAberto();

    const [pendencias, setPendencias] = useState<Notification[]>([]);
    const [minimizado, setMinimizado] = useState(false);
    const [marcandoTudo, setMarcandoTudo] = useState(false);

    // Closers e BDRs: quem recebe lead pelo rodízio (e o BDR, a empresa visitada pelo motoboy).
    const podeVer = ehComercialInterno(profile);

    const fetchPendencias = useCallback(async () => {
        if (!user?.id || !podeVer) return;
        const { data } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .eq('type', 'negotiation_assigned')
            .eq('read', false)
            .order('created_at', { ascending: true });

        const validas = (data || []).filter(ehNotificacaoDeLeadAtribuido) as Notification[];
        setPendencias(ordenarNotificacoesLead(validas));
    }, [user?.id, podeVer]);

    useEffect(() => { fetchPendencias(); }, [fetchPendencias]);

    // Depende só de user?.id (string estável), nunca do objeto `user` inteiro — o
    // AuthContext troca a referência de `user` a cada refresh de token (comum ao
    // longo de uma sessão), e depender do objeto derrubava e recriava este canal
    // toda vez. Se um lead chegasse durante essa reconexão, o aviso não aparecia
    // sozinho — só voltava a aparecer num F5 (que refaz a busca do zero). Mesmo
    // ajuste já feito em KanbanBoard.tsx (naoAbertoIds).
    useEffect(() => {
        if (!user?.id || !podeVer) return;
        const channel = supabase
            .channel(`lead-assigned-popup-${user.id}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${user.id}`,
            }, (payload) => {
                const nova = payload.new as Notification;
                if (!ehNotificacaoDeLeadAtribuido(nova)) return;
                // Sempre no fim da lista — nunca reordena o que já está na tela.
                setPendencias(prev => [...prev, nova]);
                // Lead novo chegando é motivo pra reabrir mesmo se o usuário tinha minimizado.
                setMinimizado(false);
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [user?.id, podeVer]);

    const fecharCard = (id: string) => setPendencias(prev => prev.filter(p => p.id !== id));

    const handleVerLead = (n: Notification) => {
        const id = (n.metadata as LeadAssignedMetadata | undefined)?.negociacao_id;
        if (id == null) return;
        fecharCard(n.id);
        navigate(`/crm?negociacaoId=${id}`);
    };

    const handleMarcarTudoVisto = async () => {
        if (!user || pendencias.length === 0) return;
        setMarcandoTudo(true);
        const ids = pendencias.map(p => p.id);
        setPendencias([]);
        try {
            await supabase.from('notifications').update({ read: true }).in('id', ids);
        } finally {
            setMarcandoTudo(false);
        }
    };

    useEffect(() => {
        if (minimizado === false) return;
        const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMinimizado(true); };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [minimizado]);

    if (!podeVer || pendencias.length === 0 || modalDetalheAberto || minimizado) return null;

    return (
        <div
            className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
            {...propsFundoModal(() => setMinimizado(true))}
            onKeyDown={(e) => { if (e.key === 'Escape') setMinimizado(true); }}
        >
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-5 border-b border-teal-200 dark:border-teal-800/40 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 shrink-0">
                    <div className="p-2.5 bg-teal-100 dark:bg-teal-900/40 rounded-xl">
                        <Zap size={20} className="text-teal-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-teal-900 dark:text-teal-300">
                            {pendencias.length === 1 ? 'Novo lead atribuído a você' : `${pendencias.length} novos leads atribuídos a você`}
                        </h3>
                        <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">Chegaram agora pelo rodízio automático.</p>
                    </div>
                    {pendencias.length > 1 && (
                        <button
                            onClick={handleMarcarTudoVisto}
                            disabled={marcandoTudo}
                            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors disabled:opacity-50"
                        >
                            <CheckCheck size={14} /> Marcar tudo como visto
                        </button>
                    )}
                    <button
                        onClick={() => setMinimizado(true)}
                        className="shrink-0 p-1.5 rounded-full text-teal-500 hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors"
                        title="Fechar (os leads continuam marcados até você abrir cada um)"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto custom-scrollbar p-6 space-y-4">
                    {pendencias.map(n => {
                        const meta = (n.metadata as LeadAssignedMetadata | undefined) || {};
                        const origem = fmtOrigem(meta);
                        return (
                            <div key={n.id} className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-3">
                                <div className="flex items-start justify-between gap-3">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                        {n.content.replace('Você é o responsável pela negociação: ', '')}
                                    </h4>
                                    <button
                                        onClick={() => fecharCard(n.id)}
                                        title="Dispensar (continua pulsando no Kanban até você abrir)"
                                        className="shrink-0 p-1 rounded-full text-gray-300 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800 dark:hover:text-slate-300 transition-colors"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>

                                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-600 dark:text-slate-300">
                                    <span className="flex items-center gap-1.5"><Clock size={13} className="text-gray-400" /> {fmtHorario(n.created_at)}</span>
                                    {origem && <span className="flex items-center gap-1.5"><Radio size={13} className="text-gray-400" /> {origem}</span>}
                                    {meta.valor_conta_luz && (
                                        <span className="flex items-center gap-1.5">
                                            <Receipt size={13} className="text-gray-400" /> Conta de luz: {FAIXA_CONTA_LABEL[meta.valor_conta_luz] || meta.valor_conta_luz}
                                        </span>
                                    )}
                                    {meta.reside_goias != null && (
                                        <span className="flex items-center gap-1.5">
                                            <MapPin size={13} className="text-gray-400" /> Reside em Goiás: {meta.reside_goias ? 'Sim' : 'Não'}
                                        </span>
                                    )}
                                </div>

                                <div className="flex justify-end pt-1">
                                    <button
                                        onClick={() => handleVerLead(n)}
                                        className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 transition-colors flex items-center gap-1.5"
                                    >
                                        <ArrowUpRight size={13} /> Ver lead
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default LeadAssignedPopup;
